import React, { useState } from 'react';
import { 
  Calendar, Package, FileText, CreditCard, ExternalLink, User, Clock,
  ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle, AlertCircle,
  RefreshCw, Check, Users, X
} from 'lucide-react';
import { API_URL } from '../config/api';

const GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

const FormInput = ({ label, type = 'text', value, onChange, placeholder, required, error }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-400">{label} {required && <span className="text-red-400">*</span>}</label>
    {type === 'textarea' ? (
      <textarea value={value} onChange={onChange} placeholder={placeholder} rows={3} className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-white focus:outline-none resize-none ${error ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
    ) : (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-white focus:outline-none ${error ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
    )}
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
);

export default function InsertConfigurationOfficePublic() {
  const [configStep, setConfigStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [hasVat, setHasVat] = useState(false);
  const [openingHours, setOpeningHours] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [configStaff, setConfigStaff] = useState([]);
  const [configServices, setConfigServices] = useState([]);
  const [configProducts, setConfigProducts] = useState([]);
  const [configCourses, setConfigCourses] = useState([]);
  const [configEvents, setConfigEvents] = useState([]);
  const [configSubscriptions, setConfigSubscriptions] = useState([]);
  const [wizardErrors, setWizardErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Last step is always "Presenza Online"
  const totalSteps = 2 + selectedModules.length + 1;

  const getCurrentModuleStep = () => {
    if (configStep <= 2 || configStep > 2 + selectedModules.length) return null;
    return selectedModules[configStep - 3];
  };

  const isLastStep = configStep === totalSteps;

  const toggleModule = (id) => {
    setSelectedModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  // Opening Hours
  const addOpeningHour = () => setOpeningHours([...openingHours, { id: Date.now(), day: 'Lunedì', open: '09:00', close: '18:00' }]);
  const updateOpeningHour = (id, field, val) => setOpeningHours(openingHours.map(h => h.id === id ? { ...h, [field]: val } : h));
  const removeOpeningHour = (id) => setOpeningHours(openingHours.filter(h => h.id !== id));

  // Staff
  const addStaff = () => setConfigStaff([...configStaff, { id: Date.now(), name: '', workingHours: [{ id: Date.now() + 1, day: 'Lunedì', start: '09:00', end: '18:00' }] }]);
  const updateStaff = (id, field, val) => setConfigStaff(configStaff.map(s => s.id === id ? { ...s, [field]: val } : s));
  const removeStaff = (id) => setConfigStaff(configStaff.filter(s => s.id !== id));
  const addStaffHour = (staffId) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: [...s.workingHours, { id: Date.now(), day: 'Lunedì', start: '09:00', end: '18:00' }] } : s));
  const updateStaffHour = (staffId, hourId, field, val) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: s.workingHours.map(h => h.id === hourId ? { ...h, [field]: val } : h) } : s));
  const removeStaffHour = (staffId, hourId) => setConfigStaff(configStaff.map(s => s.id === staffId ? { ...s, workingHours: s.workingHours.filter(h => h.id !== hourId) } : s));

  // Services
  const addService = () => setConfigServices([...configServices, { id: Date.now(), name: '', duration: 30, price: 0, priceType: 'fixed', priceMin: 0, priceMax: 0, operators_text: '' }]);
  const updateService = (id, field, val) => setConfigServices(configServices.map(s => s.id === id ? { ...s, [field]: val } : s));
  const removeService = (id) => setConfigServices(configServices.filter(s => s.id !== id));

  // Products
  const addProduct = () => setConfigProducts([...configProducts, { id: Date.now(), name: '', category: '', price: 0, quantity: 0, variants: '' }]);
  const updateProduct = (id, field, val) => setConfigProducts(configProducts.map(p => p.id === id ? { ...p, [field]: val } : p));
  const removeProduct = (id) => setConfigProducts(configProducts.filter(p => p.id !== id));

  // Courses
  const addCourse = () => setConfigCourses([...configCourses, { id: Date.now(), name: '', hourlyDuration: '1', day: 'Lunedì', startTime: '09:00', instructor: '', price: 0, maxParticipants: 0, linkedSubscription: '' }]);
  const updateCourse = (id, field, val) => setConfigCourses(configCourses.map(c => c.id === id ? { ...c, [field]: val } : c));
  const removeCourse = (id) => setConfigCourses(configCourses.filter(c => c.id !== id));

  // Events
  const addEvent = () => setConfigEvents([...configEvents, { id: Date.now(), name: '', description: '', date: '', time: '', price: 0, maxParticipants: 0 }]);
  const updateEvent = (id, field, val) => setConfigEvents(configEvents.map(e => e.id === id ? { ...e, [field]: val } : e));
  const removeEvent = (id) => setConfigEvents(configEvents.filter(e => e.id !== id));

  // Subscriptions
  const addSubscription = () => setConfigSubscriptions([...configSubscriptions, { id: Date.now(), name: '', price: 0, duration: '1 mese' }]);
  const updateSubscription = (id, field, val) => setConfigSubscriptions(configSubscriptions.map(s => s.id === id ? { ...s, [field]: val } : s));
  const removeSubscription = (id) => setConfigSubscriptions(configSubscriptions.filter(s => s.id !== id));

  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!formData.customer_name?.trim()) errors.customer_name = 'Campo obbligatorio';
      if (!formData.customer_email?.trim()) errors.customer_email = 'Campo obbligatorio';
      if (!formData.business_name?.trim()) errors.business_name = 'Campo obbligatorio';
    }
    if (step === 2 && selectedModules.length === 0) errors.modules = 'Seleziona almeno un modulo';
    if (step > 2 && step <= 2 + selectedModules.length) {
      const mod = selectedModules[step - 3];
      if (mod === 'appointments') configServices.forEach((s, i) => { if (!s.name?.trim()) errors[`service_name_${i}`] = 'Nome obbligatorio'; });
      if (mod === 'ecommerce') configProducts.forEach((p, i) => { if (!p.name?.trim()) errors[`product_name_${i}`] = 'Nome obbligatorio'; });
      if (mod === 'courses') configCourses.forEach((c, i) => { if (!c.name?.trim()) errors[`course_name_${i}`] = 'Nome obbligatorio'; });
      if (mod === 'events') configEvents.forEach((ev, i) => { if (!ev.name?.trim()) errors[`event_name_${i}`] = 'Nome obbligatorio'; });
    }
    setWizardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => { if (validateStep(configStep)) setConfigStep(configStep + 1); };

  const handleSave = async () => {
    if (!validateStep(configStep)) return;
    const configData = {
      ...formData, hasVat, openingHours,
      modules: selectedModules,
      staff: selectedModules.includes('appointments') ? configStaff : [],
      services: selectedModules.includes('appointments') ? configServices : [],
      products: selectedModules.includes('ecommerce') ? configProducts : [],
      courses: selectedModules.includes('courses') ? configCourses : [],
      subscriptions: selectedModules.includes('courses') ? configSubscriptions : [],
      events: selectedModules.includes('events') ? configEvents : [],
      status: 'pending'
    };
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/configurations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(configData) });
      if (!res.ok) throw new Error('Errore nel salvataggio');
      setSaved(true);
    } catch (err) { alert('Errore: ' + err.message); }
    finally { setSaving(false); }
  };

  const stepLabels = { appointments: 'Servizi', ecommerce: 'Prodotti', courses: 'Corsi', events: 'Eventi' };
  const getStepLabel = (idx) => {
    if (idx === 0) return 'Dati Attività';
    if (idx === 1) return 'Moduli';
    if (idx === totalSteps - 1) return 'Presenza Online';
    return stepLabels[selectedModules[idx - 2]] || '';
  };

  if (saved) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 max-w-lg w-full text-center" data-testid="success-message">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-emerald-400" /></div>
          <h2 className="text-2xl font-bold text-white mb-2">Registrazione completata!</h2>
          <p className="text-slate-400">La tua configurazione è stata inviata correttamente. Il nostro team la esaminerà e ti contatterà a breve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-0 md:p-4">
      <div className="bg-slate-900 md:rounded-2xl md:border border-slate-800 w-full max-w-4xl min-h-screen md:min-h-0 flex flex-col" data-testid="public-config-form">
        <div className="p-4 md:p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white">Registra la tua Attività</h1>
          <p className="text-slate-400 text-sm mt-1">Compila il modulo per configurare la tua attività sulla piattaforma Skipres</p>
        </div>

        <div className="px-4 md:px-6 py-3 border-b border-slate-700/50 bg-slate-900/80">
          <div className="flex items-center gap-0">
            {[...Array(Math.max(2, totalSteps))].map((_, idx) => {
              const stepNum = idx + 1;
              const isActive = stepNum === configStep;
              const isCompleted = stepNum < configStep;
              return (
                <React.Fragment key={idx}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${isCompleted ? 'bg-emerald-500 text-white' : isActive ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : stepNum}
                    </div>
                    {isActive && <span className="text-xs text-slate-400 mt-1 whitespace-nowrap">{getStepLabel(idx)}</span>}
                  </div>
                  {idx < Math.max(1, totalSteps - 1) && <div className={`flex-1 h-0.5 mx-1 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-700'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4 flex-1 overflow-y-auto">

          {/* ============ STEP 1: DATI ATTIVITA ============ */}
          {configStep === 1 && (
            <>
              <h4 className="text-white font-semibold flex items-center gap-2"><User className="w-4 h-4 text-emerald-400" /> Dati Cliente</h4>
              <p className="text-slate-500 text-xs mb-2">Informazioni del referente principale dell'attività</p>
              <div className="grid md:grid-cols-3 gap-4">
                <FormInput label="Nome e Cognome" value={formData.customer_name || ''} onChange={e => setFormData({...formData, customer_name: e.target.value})} placeholder="Es. Mario Rossi" required error={wizardErrors.customer_name} />
                <FormInput label="Email" type="email" value={formData.customer_email || ''} onChange={e => setFormData({...formData, customer_email: e.target.value})} placeholder="email@esempio.it" required error={wizardErrors.customer_email} />
                <FormInput label="Telefono" value={formData.customer_phone || ''} onChange={e => setFormData({...formData, customer_phone: e.target.value})} placeholder="+39 333 1234567" />
              </div>

              <h4 className="text-white font-semibold pt-4 flex items-center gap-2"><Package className="w-4 h-4 text-amber-400" /> Dati Attività</h4>
              <p className="text-slate-500 text-xs mb-2">Informazioni sulla sede e tipologia di attività</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="Nome Attività" value={formData.business_name || ''} onChange={e => setFormData({...formData, business_name: e.target.value})} placeholder="Es. Salone Bellezza Anna" required error={wizardErrors.business_name} />
                <FormInput label="Tipo Attività" value={formData.business_type || ''} onChange={e => setFormData({...formData, business_type: e.target.value})} placeholder="Es. Parrucchiere, Ristorante, SPA..." />
                <FormInput label="Indirizzo Sede" value={formData.business_address || ''} onChange={e => setFormData({...formData, business_address: e.target.value})} placeholder="Via Roma 1" />
                <FormInput label="Città" value={formData.business_city || ''} onChange={e => setFormData({...formData, business_city: e.target.value})} placeholder="Es. Napoli" />
                <FormInput label="CAP" value={formData.business_cap || ''} onChange={e => setFormData({...formData, business_cap: e.target.value})} placeholder="Es. 80100" />
                <FormInput label="Provincia" value={formData.business_province || ''} onChange={e => setFormData({...formData, business_province: e.target.value})} placeholder="Es. NA" />
              </div>

              {/* Orari di Apertura */}
              <h4 className="text-white font-semibold pt-4 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> Orari di Apertura</h4>
              <p className="text-slate-500 text-xs mb-2">Seleziona i giorni e gli orari di apertura della sede</p>
              {openingHours.map((h) => (
                <div key={h.id} className="flex items-center gap-2 mb-2">
                  <select value={h.day} onChange={e => updateOpeningHour(h.id, 'day', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                    {GIORNI.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <input type="time" value={h.open} onChange={e => updateOpeningHour(h.id, 'open', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                  <span className="text-slate-500">—</span>
                  <input type="time" value={h.close} onChange={e => updateOpeningHour(h.id, 'close', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                  <button onClick={() => removeOpeningHour(h.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addOpeningHour} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm"><Plus className="w-4 h-4" /> Aggiungi giorno</button>

              {/* Dati Fiscali con checkbox */}
              <div className="pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={hasVat} onChange={e => setHasVat(e.target.checked)} className="w-4 h-4 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 bg-slate-800" />
                  <span className="text-white font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Ho la Partita IVA</span>
                </label>
              </div>
              {hasVat && (
                <>
                  <p className="text-slate-500 text-xs mb-2">Dati per la fatturazione elettronica</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <FormInput label="Partita IVA" value={formData.vat_number || ''} onChange={e => setFormData({...formData, vat_number: e.target.value})} placeholder="Es. IT12345678901" />
                    <FormInput label="Codice Fiscale" value={formData.fiscal_code || ''} onChange={e => setFormData({...formData, fiscal_code: e.target.value})} placeholder="Es. RSSMRA80A01H501Z" />
                    <FormInput label="Ragione Sociale" value={formData.legal_name || ''} onChange={e => setFormData({...formData, legal_name: e.target.value})} placeholder="Denominazione legale dell'azienda" />
                    <FormInput label="Codice SDI" value={formData.sdi_code || ''} onChange={e => setFormData({...formData, sdi_code: e.target.value})} placeholder="Codice destinatario (7 caratteri)" />
                    <FormInput label="PEC" type="email" value={formData.pec || ''} onChange={e => setFormData({...formData, pec: e.target.value})} placeholder="pec@esempio.it" />
                    <FormInput label="Regime Fiscale" value={formData.tax_regime || ''} onChange={e => setFormData({...formData, tax_regime: e.target.value})} placeholder="Es. Ordinario, Forfettario..." />
                  </div>
                </>
              )}

              <h4 className="text-white font-semibold pt-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-400" /> Dati Pagamento</h4>
              <p className="text-slate-500 text-xs mb-2">Coordinate bancarie per accrediti e pagamenti</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="IBAN" value={formData.iban || ''} onChange={e => setFormData({...formData, iban: e.target.value})} placeholder="IT00X0000000000000000000000" />
                <FormInput label="Intestatario Conto" value={formData.account_holder || ''} onChange={e => setFormData({...formData, account_holder: e.target.value})} placeholder="Nome intestatario del conto" />
              </div>
            </>
          )}

          {/* ============ STEP 2: SELEZIONE MODULI ============ */}
          {configStep === 2 && (
            <>
              <h4 className="text-white font-semibold mb-4">Quali servizi vuole offrire la tua attività?</h4>
              <p className="text-slate-400 text-sm mb-6">Seleziona uno o più moduli da attivare.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { id: 'appointments', icon: Calendar, title: 'Servizi a Prenotazione', desc: 'Prenotazioni con slot orari. Ideale per parrucchieri, estetiste, medici, consulenti.' },
                  { id: 'ecommerce', icon: Package, title: 'E-commerce / Prodotti', desc: 'Vendita prodotti con spedizione, ritiro in sede o consegna locale.' },
                  { id: 'courses', icon: FileText, title: 'Corsi', desc: 'Corsi con programmazione settimanale. Yoga, fitness, formazione, etc.' },
                  { id: 'events', icon: Calendar, title: 'Eventi', desc: 'Eventi singoli con data e ora. Workshop, serate, degustazioni.' }
                ].map(mod => {
                  const sel = selectedModules.includes(mod.id);
                  const Icon = mod.icon;
                  return (
                    <div key={mod.id} onClick={() => toggleModule(mod.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${sel ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}
                      data-testid={`module-${mod.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sel ? 'bg-emerald-500' : 'bg-slate-700'}`}><Icon className="w-5 h-5 text-white" /></div>
                        <div className="flex-1">
                          <h5 className="text-white font-semibold">{mod.title}</h5>
                          <p className="text-slate-400 text-sm mt-1">{mod.desc}</p>
                        </div>
                        {sel && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                      </div>
                    </div>
                  );
                })}
              </div>
              {wizardErrors.modules && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" /><span className="text-red-400 text-sm">{wizardErrors.modules}</span>
                </div>
              )}
            </>
          )}

          {/* ============ STEP 3+: SERVIZI A PRENOTAZIONE ============ */}
          {configStep > 2 && getCurrentModuleStep() === 'appointments' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" /> Configura Servizi a Prenotazione</h4>

              {/* Staff */}
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-4">
                <h5 className="text-white font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Staff</h5>
                <p className="text-slate-500 text-xs mb-3">Inserisci le persone/risorse che erogano i servizi con i relativi orari lavorativi.</p>
                {configStaff.map((staff) => (
                  <div key={staff.id} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700 mb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Nome e Cognome</label>
                        <input type="text" placeholder="Es. Marco Bianchi" value={staff.name} onChange={e => updateStaff(staff.id, 'name', e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                      </div>
                      <button onClick={() => removeStaff(staff.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 mt-5"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <label className="text-xs text-slate-500 mb-1 block">Orari lavorativi</label>
                    {staff.workingHours.map(wh => (
                      <div key={wh.id} className="flex items-center gap-2 mb-2">
                        <select value={wh.day} onChange={e => updateStaffHour(staff.id, wh.id, 'day', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none">
                          {GIORNI.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <input type="time" value={wh.start} onChange={e => updateStaffHour(staff.id, wh.id, 'start', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none" />
                        <span className="text-slate-500 text-xs">—</span>
                        <input type="time" value={wh.end} onChange={e => updateStaffHour(staff.id, wh.id, 'end', e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white text-xs focus:border-emerald-500 focus:outline-none" />
                        <button onClick={() => removeStaffHour(staff.id, wh.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => addStaffHour(staff.id)} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs"><Plus className="w-3 h-3" /> Aggiungi fascia oraria</button>
                  </div>
                ))}
                <button onClick={addStaff} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-2"><Plus className="w-4 h-4" /> Aggiungi membro staff</button>
              </div>

              {/* Servizi */}
              <h5 className="text-white font-semibold mb-2">Elenco Servizi</h5>
              {configServices.map((service, idx) => (
                <div key={service.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">Servizio #{idx + 1}</span>
                    <button onClick={() => removeService(service.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nome servizio <span className="text-red-400">*</span></label>
                      <input type="text" placeholder="Es. Taglio uomo, Piega donna..." value={service.name} onChange={e => updateService(service.id, 'name', e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`service_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
                      {wizardErrors[`service_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`service_name_${idx}`]}</p>}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Durata (minuti)</label>
                      <input type="number" placeholder="Es. 30" value={service.duration} onChange={e => updateService(service.id, 'duration', parseInt(e.target.value) || 0)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Tipo prezzo</label>
                      <select value={service.priceType} onChange={e => updateService(service.id, 'priceType', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                        <option value="fixed">Prezzo fisso</option>
                        <option value="variable">Prezzo variabile (min - max)</option>
                        <option value="from">A partire da</option>
                      </select>
                    </div>
                    {service.priceType === 'fixed' && (
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label>
                        <div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span>
                          <input type="number" placeholder="Es. 25.00" value={service.price} onChange={e => updateService(service.id, 'price', parseFloat(e.target.value) || 0)}
                            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                        </div>
                      </div>
                    )}
                    {service.priceType === 'variable' && (
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <label className="text-xs text-slate-500 mb-1 block">Da (€)</label>
                          <input type="number" placeholder="Min" value={service.priceMin || ''} onChange={e => updateService(service.id, 'priceMin', parseFloat(e.target.value) || 0)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-slate-500 mb-1 block">A (€)</label>
                          <input type="number" placeholder="Max" value={service.priceMax || ''} onChange={e => updateService(service.id, 'priceMax', parseFloat(e.target.value) || 0)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                        </div>
                      </div>
                    )}
                    {service.priceType === 'from' && (
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">A partire da (€)</label>
                        <div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span>
                          <input type="number" placeholder="Es. 20.00" value={service.price} onChange={e => updateService(service.id, 'price', parseFloat(e.target.value) || 0)}
                            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Risorse / Operatori</label>
                    <input type="text" placeholder="Es. Marco, Sala 1, Lettino A, Macchina laser..." value={service.operators_text || ''} onChange={e => updateService(service.id, 'operators_text', e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    <p className="text-slate-600 text-xs mt-1">Persone, sale, macchinari o postazioni che erogano questo servizio</p>
                  </div>
                </div>
              ))}
              <button onClick={addService} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Aggiungi Servizio
              </button>
            </>
          )}

          {/* ============ STEP 3+: E-COMMERCE ============ */}
          {configStep > 2 && getCurrentModuleStep() === 'ecommerce' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Package className="w-5 h-5 text-amber-400" /> Configura Prodotti E-commerce</h4>
              <p className="text-slate-400 text-sm mb-4">Inserisci i prodotti in vendita con prezzo, disponibilità e varianti.</p>
              {configProducts.map((product, idx) => (
                <div key={product.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">Prodotto #{idx + 1}</span>
                    <button onClick={() => removeProduct(product.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nome prodotto <span className="text-red-400">*</span></label>
                      <input type="text" placeholder="Es. Shampoo Professionale..." value={product.name} onChange={e => updateProduct(product.id, 'name', e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`product_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
                      {wizardErrors[`product_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`product_name_${idx}`]}</p>}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Categoria</label>
                      <input type="text" placeholder="Es. Cura capelli, Cosmetici..." value={product.category} onChange={e => updateProduct(product.id, 'category', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label>
                      <div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span>
                        <input type="number" placeholder="19.90" value={product.price} onChange={e => updateProduct(product.id, 'price', parseFloat(e.target.value) || 0)}
                          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Quantità</label>
                      <input type="number" placeholder="50" value={product.quantity} onChange={e => updateProduct(product.id, 'quantity', parseInt(e.target.value) || 0)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Varianti</label>
                      <input type="text" placeholder="Es. S, M, L" value={product.variants} onChange={e => updateProduct(product.id, 'variants', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addProduct} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Aggiungi Prodotto
              </button>
            </>
          )}

          {/* ============ STEP 3+: CORSI + ABBONAMENTI ============ */}
          {configStep > 2 && getCurrentModuleStep() === 'courses' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-400" /> Configura Corsi</h4>
              <p className="text-slate-400 text-sm mb-4">Inserisci i corsi con durata oraria, giorno, orario e istruttore.</p>

              {/* Abbonamenti */}
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-4">
                <h5 className="text-white font-semibold mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-400" /> Abbonamenti</h5>
                <p className="text-slate-500 text-xs mb-3">Definisci i piani di abbonamento disponibili. Potrai collegare ogni corso ad un abbonamento.</p>
                {configSubscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-2 mb-2">
                    <input type="text" placeholder="Nome abbonamento (es. Mensile Base)" value={sub.name} onChange={e => updateSubscription(sub.id, 'name', e.target.value)}
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 text-sm">€</span>
                      <input type="number" placeholder="Prezzo" value={sub.price || ''} onChange={e => updateSubscription(sub.id, 'price', parseFloat(e.target.value) || 0)}
                        className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <select value={sub.duration} onChange={e => updateSubscription(sub.id, 'duration', e.target.value)}
                      className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                      <option value="1 settimana">1 settimana</option>
                      <option value="1 mese">1 mese</option>
                      <option value="3 mesi">3 mesi</option>
                      <option value="6 mesi">6 mesi</option>
                      <option value="1 anno">1 anno</option>
                    </select>
                    <button onClick={() => removeSubscription(sub.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addSubscription} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-2"><Plus className="w-4 h-4" /> Aggiungi abbonamento</button>
              </div>

              {/* Corsi */}
              {configCourses.map((course, idx) => (
                <div key={course.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">Corso #{idx + 1}</span>
                    <button onClick={() => removeCourse(course.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nome corso <span className="text-red-400">*</span></label>
                      <input type="text" placeholder="Es. Yoga Mattutino, Pilates Base..." value={course.name} onChange={e => updateCourse(course.id, 'name', e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`course_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
                      {wizardErrors[`course_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`course_name_${idx}`]}</p>}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Istruttore / Docente</label>
                      <input type="text" placeholder="Es. Dott. Rossi, Coach Anna..." value={course.instructor} onChange={e => updateCourse(course.id, 'instructor', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Durata lezione</label>
                      <select value={course.hourlyDuration} onChange={e => updateCourse(course.id, 'hourlyDuration', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                        <option value="0.5">30 min</option>
                        <option value="1">1h</option>
                        <option value="1.5">1h 30min</option>
                        <option value="2">2h</option>
                        <option value="2.5">2h 30min</option>
                        <option value="3">3h</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Giorno</label>
                      <select value={course.day} onChange={e => updateCourse(course.id, 'day', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                        {GIORNI.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Ora inizio</label>
                      <input type="time" value={course.startTime} onChange={e => updateCourse(course.id, 'startTime', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Posti max</label>
                      <input type="number" placeholder="15" value={course.maxParticipants} onChange={e => updateCourse(course.id, 'maxParticipants', parseInt(e.target.value) || 0)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Prezzo singola lezione (€)</label>
                      <div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span>
                        <input type="number" placeholder="15.00" value={course.price || ''} onChange={e => updateCourse(course.id, 'price', parseFloat(e.target.value) || 0)}
                          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Abbonamento collegato</label>
                      <select value={course.linkedSubscription || ''} onChange={e => updateCourse(course.id, 'linkedSubscription', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none">
                        <option value="">Nessun abbonamento</option>
                        {configSubscriptions.filter(s => s.name).map(s => (
                          <option key={s.id} value={s.id}>{s.name} — €{s.price}/{s.duration}</option>
                        ))}
                      </select>
                      {configSubscriptions.length === 0 && <p className="text-slate-600 text-xs mt-1">Aggiungi prima un abbonamento nella sezione sopra</p>}
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addCourse} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Aggiungi Corso
              </button>
            </>
          )}

          {/* ============ STEP 3+: EVENTI ============ */}
          {configStep > 2 && getCurrentModuleStep() === 'events' && (
            <>
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-400" /> Configura Eventi</h4>
              <p className="text-slate-400 text-sm mb-4">Inserisci gli eventi con data, ora, prezzo e capienza massima.</p>
              {configEvents.map((event, idx) => (
                <div key={event.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-medium">Evento #{idx + 1}</span>
                    <button onClick={() => removeEvent(event.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nome evento <span className="text-red-400">*</span></label>
                      <input type="text" placeholder="Es. Workshop Ceramica, Serata Jazz..." value={event.name} onChange={e => updateEvent(event.id, 'name', e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none ${wizardErrors[`event_name_${idx}`] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'}`} />
                      {wizardErrors[`event_name_${idx}`] && <p className="text-red-400 text-xs mt-1">{wizardErrors[`event_name_${idx}`]}</p>}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Descrizione breve</label>
                      <input type="text" placeholder="Es. Una serata speciale dedicata a..." value={event.description || ''} onChange={e => updateEvent(event.id, 'description', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Data</label>
                      <input type="date" value={event.date} onChange={e => updateEvent(event.id, 'date', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Ora</label>
                      <input type="time" value={event.time} onChange={e => updateEvent(event.id, 'time', e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Prezzo (€)</label>
                      <div className="flex items-center gap-2"><span className="text-slate-400 text-sm">€</span>
                        <input type="number" placeholder="0=gratis" value={event.price} onChange={e => updateEvent(event.id, 'price', parseFloat(e.target.value) || 0)}
                          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Posti max</label>
                      <input type="number" placeholder="50" value={event.maxParticipants} onChange={e => updateEvent(event.id, 'maxParticipants', parseInt(e.target.value) || 0)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addEvent} className="w-full p-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors flex items-center justify-center gap-2">
                <Plus className="w-5 h-5" /> Aggiungi Evento
              </button>
            </>
          )}

          {/* ============ ULTIMO STEP: PRESENZA ONLINE ============ */}
          {isLastStep && (
            <>
              <h4 className="text-white font-semibold flex items-center gap-2"><ExternalLink className="w-4 h-4 text-purple-400" /> Presenza Online</h4>
              <p className="text-slate-500 text-xs mb-2">Link ai profili social e web dell'attività (opzionali)</p>
              <div className="grid md:grid-cols-2 gap-4">
                <FormInput label="Sito Web" value={formData.website || ''} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="https://www.miosito.it" />
                <FormInput label="Instagram" value={formData.instagram || ''} onChange={e => setFormData({...formData, instagram: e.target.value})} placeholder="@nomeutente" />
                <FormInput label="Facebook" value={formData.facebook || ''} onChange={e => setFormData({...formData, facebook: e.target.value})} placeholder="https://facebook.com/pagina" />
                <FormInput label="Google Maps" value={formData.google_maps || ''} onChange={e => setFormData({...formData, google_maps: e.target.value})} placeholder="Link alla scheda Google Maps" />
              </div>
              <FormInput label="Note Aggiuntive" type="textarea" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Informazioni aggiuntive, richieste particolari..." />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-slate-800 flex justify-between items-center mt-auto sticky bottom-0 bg-slate-900">
          <button onClick={() => configStep > 1 ? setConfigStep(configStep - 1) : null} disabled={configStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${configStep === 1 ? 'border-slate-800 text-slate-600 cursor-not-allowed' : 'border-slate-600 text-white hover:bg-slate-800'}`}
            data-testid="wizard-back-btn">
            <ChevronLeft className="w-4 h-4" /> Indietro
          </button>
          <span className="text-slate-500 text-sm">Step {configStep} di {totalSteps}</span>
          {!isLastStep ? (
            <button onClick={handleNextStep} className="flex items-center gap-2 px-6 py-2 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg transition-colors" data-testid="wizard-next-btn">
              Avanti <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white rounded-lg transition-colors" data-testid="wizard-save-btn">
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {saving ? 'Invio...' : 'Invia Registrazione'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
